/** Full-screen order ticket for the mobile instrument screen. Same rules and submit path as the desktop order panel. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { D, fmt, fmtPrice, fmtUsd, pairLabel, type Side } from '@platform/shared';
import { LeverageModal } from '../OrderPanel';
import { cx, fmtLots, Icon, sideLabel, Spinner, unitLabel } from '../ui';
import { HelpTip } from '../HelpTip';
import { orderHelp } from '../orderHelp';
import { EventWarning } from '../EventNotice';
import { ctaTone, OrderProblemNote } from '../OrderProblemNote';
import { DEC_RE, useOrderForm } from '../useOrderForm';
import { roundCls, SwitchTrack } from './chrome';
import { NumInput } from '../NumInput';
import { InstrumentIcon } from '../InstrumentIcon';

const PCTS: [number, string][] = [
  [10, '10%'],
  [25, '25%'],
  [50, '50%'],
  [100, 'Max'],
];
const LOT_PICKS = ['0.01', '0.1', '0.5', '1'];

function SummaryRow({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="flex items-center gap-1.5 text-muted">{label}</dt>
      <dd className={cx('tnum m-0 text-right font-semibold', className)}>{children}</dd>
    </div>
  );
}

export function OrderTicket({ symbol, initialSide, onClose, onDone }: { symbol: string; initialSide: Side; onClose: () => void; onDone: (message: string) => void }) {
  const f = useOrderForm(symbol, initialSide);
  const [levOpen, setLevOpen] = useState(false);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const levOpenRef = useRef(levOpen);
  levOpenRef.current = levOpen;

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !levOpenRef.current && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [onClose]);

  const long = f.side === 'long';
  const unit = f.inst?.unit ?? symbol;
  const help = orderHelp(f);
  const blocked = !!f.problem;
  const bad = (field: string) => f.problem?.field === field;
  const usdPriced = f.ccy === 'USD';
  const lowKind = long ? 'Limit' : 'Stop';
  const highKind = long ? 'Stop' : 'Limit';
  const kindHint = (k: string) => {
    const name = `${sideLabel(f.side)} ${k}`;
    return f.pendType && f.orderLabel === name ? <b className="font-semibold text-fg">{name}</b> : name;
  };
  const freeAfter = f.calc && f.me ? D.from(f.available).sub(f.calc.margin).sub(f.calc.fee) : null;

  const submit = async () => {
    const ok = await f.submit();
    if (ok) onDone(ok);
  };

  const priceInput = (value: string, onChange: (v: string) => void, label: string) => (
    <span className="flex items-baseline gap-0.5">
      {usdPriced && <span className="text-[17px] font-semibold text-muted">$</span>}
      <NumInput
      value={value}
      onValue={(v) => DEC_RE.test(v) && onChange(v)}
      placeholder="None"
      aria-label={label}
      className="tnum h-7 w-full min-w-0 bg-transparent text-[17px] font-semibold text-fg outline-none placeholder:font-normal placeholder:text-muted"
      />
    </span>
  );
  const bigInput = 'tnum h-[52px] min-w-0 flex-1 bg-transparent text-[42px] font-semibold tracking-[-0.03em] text-fg outline-none placeholder:text-line-strong';

  return (
    <div role="dialog" aria-modal="true" aria-label={`New ${pairLabel(symbol)} order`} className="sheet-in fixed inset-0 z-50 flex flex-col bg-bg text-fg">
      <div className="mx-auto flex min-h-0 w-full max-w-[640px] flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 px-4 pt-[calc(env(safe-area-inset-top)+12px)]">
          <button ref={closeBtn} type="button" onClick={onClose} aria-label="Close order ticket" className={roundCls}>
            <Icon name="close" size={22} strokeWidth={2.2} />
          </button>
          <div className="min-w-0 text-center">
            <h2 className="m-0 text-[17px] leading-[22px] font-semibold">New order</h2>
            <div className="tnum flex items-center justify-center gap-1.5 truncate text-[13px] text-muted">
              <InstrumentIcon symbol={symbol} size={16} />
              {pairLabel(symbol)} · Mark {f.quote ? fmtUsd(f.quote.mark, f.dp) : '—'}
              {f.account?.kind === 'demo' && ' · Demo'}
            </div>
          </div>
          <span aria-hidden="true" className="w-12 shrink-0" />
        </header>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="no-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain pb-3">
            <div role="radiogroup" aria-label="Direction" className="mx-4 mt-5 grid grid-cols-2 rounded-full bg-track p-1">
              {(['long', 'short'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={f.side === s}
                  onClick={() => f.setSide(s)}
                  className={cx('h-12 rounded-full text-[17px] font-semibold', f.side === s ? cx('bg-card shadow-float', s === 'long' ? 'text-up' : 'text-down') : 'text-muted')}
                >
                  {sideLabel(s)}
                </button>
              ))}
            </div>

            <div className="mx-5 mt-5 flex items-center justify-between gap-3">
              <span className={cx('flex items-center gap-1.5 text-[15px] font-semibold', bad('size') ? 'text-warn' : 'text-muted')}>
                <label htmlFor="ticket-size">{f.byLots ? 'Volume' : 'Amount'}</label>
                <HelpTip help={help.size} />
              </span>
              <div role="radiogroup" aria-label="Order size in" className="flex rounded-full bg-track p-[3px]">
                {(['lots', 'usd'] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    role="radio"
                    aria-checked={f.unit === u}
                    onClick={() => f.setUnit(u)}
                    className={cx('h-8 rounded-full px-3.5 text-[14px] font-semibold', f.unit === u ? 'bg-card text-fg shadow-float' : 'text-muted')}
                  >
                    {u === 'lots' ? 'Lots' : 'USDT'}
                  </button>
                ))}
              </div>
            </div>

            {f.byLots ? (
              <>
                <div className="mx-5 mt-1 flex items-center gap-3">
                  <button type="button" onClick={() => f.stepLots(-1)} aria-label="Decrease volume" className="grid size-11 shrink-0 place-items-center rounded-full bg-track text-[22px] font-semibold text-fg">
                    −
                  </button>
                  <NumInput
                    id="ticket-size"
                    value={f.lots}
                    onValue={(v) => DEC_RE.test(v) && f.setLots(v)}
                    placeholder={D.from(f.minLots).toFixed(f.lotDp)}
                    className={cx(bigInput, 'text-center')}
                  />
                  <button type="button" onClick={() => f.stepLots(1)} aria-label="Increase volume" className="grid size-11 shrink-0 place-items-center rounded-full bg-track text-[22px] font-semibold text-fg">
                    +
                  </button>
                </div>
                <div className="tnum mx-5 mt-1 text-center text-[14px] text-muted">
                  lots · 1 lot = {fmt(f.lotSize, 0)} {unitLabel(unit, f.lotSize)} · Max {fmt(f.maxLots, f.lotDp)}
                </div>
                <div className="mx-4 mt-3.5 grid grid-cols-4 gap-2">
                  {LOT_PICKS.filter((v) => D.from(v).gte(f.minLots)).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => f.setLots(D.from(v).toFixed(f.lotDp))}
                      className={cx('tnum h-10 rounded-full text-[15px] font-semibold', f.lotsD.eq(v) ? 'bg-fg text-bg' : 'bg-track text-fg')}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="mx-5 mt-1 flex items-baseline gap-1">
                  <span className="text-[42px] font-semibold text-muted">$</span>
                  <NumInput id="ticket-size" value={f.amount} onValue={(v) => DEC_RE.test(v) && f.setAmount(v)} placeholder="0.00" className={bigInput} />
                </div>
                <div className="tnum mx-5 text-[14px] text-muted">
                  Available {fmtUsd(f.available)} · Max {fmtUsd(f.maxN.round(2, 'down'))}
                </div>
                <div className="mx-4 mt-3.5 grid grid-cols-4 gap-2">
                  {PCTS.map(([p, label]) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => f.setPct(p)}
                      disabled={!f.maxN.isPos()}
                      className={cx('h-10 rounded-full text-[15px] font-semibold disabled:opacity-50', Math.round(f.pct) === p && f.amt.isPos() ? 'bg-fg text-bg' : 'bg-track text-fg')}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="mx-4 mt-[18px] rounded-[20px] border border-line px-4">
              <div className="flex h-14 items-center justify-between gap-3 border-b border-line">
                <span className="flex items-center gap-1.5 text-[16px]">
                  Order type <HelpTip help={help.type} />
                </span>
                <div role="radiogroup" aria-label="Order type" className="flex rounded-full bg-track p-[3px]">
                  {(['market', 'pending'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={f.type === t}
                      onClick={() => f.setType(t)}
                      className={cx('h-[34px] rounded-full px-3.5 text-[14px] font-semibold', f.type === t ? 'bg-card text-fg shadow-float' : 'text-muted')}
                    >
                      {t === 'market' ? 'Market' : 'Limit / Stop'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex min-h-12 items-center justify-between gap-3 border-b border-line py-2 text-[14px]">
                <span className="text-muted">Order</span>
                <span className={cx('rounded-full px-2.5 py-0.5 text-[13px] font-bold tracking-wide uppercase', long ? 'bg-up-bg text-up' : 'bg-down-bg text-down')}>
                  {f.orderLabel}
                </span>
              </div>
              {f.type === 'pending' && (
                <div className="flex flex-col gap-1 border-b border-line py-2">
                  <div className="flex min-h-10 items-center justify-between gap-3">
                    <span className="flex items-center gap-1.5 text-[16px]">
                      <label htmlFor="ticket-limit">Order price</label>
                      <HelpTip help={help.price} />
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => f.quote && f.setLimit(D.from(long ? f.quote.bid : f.quote.ask).toFixed(f.dp))}
                        className="text-[14px] font-semibold text-accent-fg"
                      >
                        {long ? 'Bid' : 'Ask'}
                      </button>
                      <span className="relative block">
                        {usdPriced && (
                          <span aria-hidden="true" className="tnum pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[16px] font-semibold text-muted">
                            $
                          </span>
                        )}
                        <NumInput
                          id="ticket-limit"
                          value={f.limit}
                          onValue={(v) => DEC_RE.test(v) && f.setLimit(v)}
                          placeholder={f.quote ? fmt(f.quote.mark, f.dp) : '0.00'}
                          className={cx(
                            'tnum h-10 w-[150px] rounded-xl border bg-bg py-0 pr-3 text-right text-[16px] font-semibold text-fg outline-none placeholder:font-normal placeholder:text-muted',
                            usdPriced ? 'pl-6' : 'pl-3',
                            bad('price') ? 'border-warn' : 'border-line-strong focus:border-fg',
                          )}
                        />
                      </span>
                    </div>
                  </div>
                  {f.openRef && (
                    <span className="tnum text-[13px] text-muted">
                      Below {fmtPrice(f.openRef, f.dp, f.ccy)} → {kindHint(lowKind)} · Above → {kindHint(highKind)}
                    </span>
                  )}
                </div>
              )}
              <button type="button" onClick={() => setLevOpen(true)} className="flex h-14 w-full items-center justify-between gap-3 border-b border-line text-left text-fg">
                <span className="text-[16px]">Leverage</span>
                <span className="tnum flex items-center gap-1 text-[16px] font-semibold">
                  {f.lev}x
                  <Icon name="right" size={18} strokeWidth={2.4} />
                </span>
              </button>
              <button type="button" role="switch" aria-checked={f.tpsl} onClick={() => f.setTpsl(!f.tpsl)} className="flex h-14 w-full items-center justify-between gap-3 text-left text-fg">
                <span className="text-[16px]">Take profit / Stop loss</span>
                <SwitchTrack on={f.tpsl} />
              </button>
              <p className="m-0 -mt-2 flex items-center gap-1.5 pb-3 text-[13px] text-muted">
                What are these? <HelpTip help={help.tpsl} />
              </p>
              {f.tpsl && (
                <div className="grid grid-cols-2 gap-2.5 pb-4">
                  <label className={cx('block rounded-[14px] border bg-panel px-3 py-2.5', bad('tp') ? 'border-warn' : 'border-transparent')}>
                    <span className="block text-[13px] font-semibold text-up">Take profit</span>
                    {priceInput(f.tpVals.price, (v) => f.setTp(v ? { kind: 'price', value: v } : null), 'Take profit price')}
                    <span className="tnum block text-[13px] text-muted">{f.tpVals.pct ? `${f.tpVals.pct}% from entry` : 'Price to close in profit'}</span>
                  </label>
                  <label className={cx('block rounded-[14px] border bg-panel px-3 py-2.5', bad('sl') ? 'border-warn' : 'border-transparent')}>
                    <span className="block text-[13px] font-semibold text-down">Stop loss</span>
                    {priceInput(f.slVals.price, (v) => f.setSl(v ? { kind: 'price', value: v } : null), 'Stop loss price')}
                    <span className="tnum block text-[13px] text-muted">{f.slVals.pct ? `${f.slVals.pct}% from entry` : 'Price to limit the loss'}</span>
                  </label>
                </div>
              )}
            </div>

            <dl className="mx-5 mt-4 flex flex-col gap-2 text-[15px]">
              <SummaryRow label={f.type === 'pending' ? (f.pendType ? `${f.orderLabel} price` : 'Order price') : `Entry (${long ? 'ask' : 'bid'})`}>{f.ref ? fmtPrice(f.ref, f.dp, f.ccy) : '—'}</SummaryRow>
              <SummaryRow label="Position">
                {f.calc ? `${fmtLots(f.calc.qty, f.lotSize)} lots · ${fmt(f.calc.qty, f.qdp, 'down')} ${unitLabel(unit, f.calc.qty)}` : '—'}
              </SummaryRow>
              {f.byLots && (
                <SummaryRow label={<>Notional <HelpTip help={help.notional} /></>}>{fmtUsd(f.amt)}</SummaryRow>
              )}
              <SummaryRow label={<>{f.calc?.hedges ? 'Margin (hedged)' : 'Margin'} <HelpTip help={help.margin} /></>}>{fmtUsd(f.calc ? f.calc.margin : 0)}</SummaryRow>
              <SummaryRow label={<>Fee{f.feeLabel && ` (${f.feeLabel})`} <HelpTip help={help.fee} /></>}>{fmtUsd(f.calc ? f.calc.fee : 0)}</SummaryRow>
              <SummaryRow label={<>Free margin after <HelpTip help={help.free} /></>} className={freeAfter?.isNeg() ? 'text-warn' : undefined}>
                {freeAfter ? fmtUsd(freeAfter) : '—'}
              </SummaryRow>
              <SummaryRow label={<>Value of $1 move <HelpTip help={help.pointValue} /></>}>{fmtUsd(f.calc ? f.calc.pointValue : 0)}</SummaryRow>
            </dl>
          </div>

          <div className="border-t border-line px-4 pt-3 pb-[max(20px,env(safe-area-inset-bottom))]">
            <div className="mb-2 empty:hidden">
              <EventWarning symbol={symbol} />
            </div>
            {f.problem && <OrderProblemNote problem={f.problem} className="mb-2 text-[13px]" />}
            {!f.problem && f.msg && !f.msg.ok && (
              <p role="alert" className="m-0 mb-2 text-center text-[14px] text-down">
                {f.msg.text}
              </p>
            )}
            <button
              type="submit"
              disabled={f.busy || blocked}
              className={cx('tnum flex h-14 w-full items-center justify-center rounded-full px-4 text-[17px] font-semibold disabled:cursor-not-allowed', ctaTone(f.side, blocked))}
            >
              {f.busy ? <Spinner /> : <span className="truncate">{f.ctaLabel}</span>}
            </button>
            <p className="m-0 mt-2 text-center text-[12px] text-muted">CFDs are leveraged. You can lose your whole margin quickly.</p>
          </div>
        </form>
      </div>

      {f.inst && (
        <LeverageModal open={levOpen} onClose={() => setLevOpen(false)} value={f.lev} min={f.inst.minLeverage} max={f.maxLev} onConfirm={f.setLev} available={f.available} feeRate={f.feeRate} feeFlat={f.feeFlat} />
      )}
    </div>
  );
}
